/**
 * GovKnowledgeBase — Base de Conhecimento Institucional.
 *
 * Sprint 2 · v0.98 · Fev/2026.
 *
 * - Lista documentos oficiais organizados por Secretaria
 * - Upload de arquivos (PDF, DOC, XLS, imagens, texto)
 * - Upload por texto puro (FAQ, comunicados curtos)
 * - Busca semântica institucional
 * - Filtro por secretaria
 * - Edição inline de secretaria/tags/notas
 * - Exclusão
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// v0.99.17 · Fev/2026 — Badge de estado do doc v2 (pipeline OCR/Qdrant).
// Só aparece para docs v2 (PDFs grandes). Mercado privado tem UI própria;
// aqui replicamos a mesma linguagem visual pra consistência com GovKB.
const _KB_STATE_META = {
  uploaded:             { label: "Enfileirado",   cls: "bg-slate-100 text-slate-700" },
  queued:               { label: "Enfileirado",   cls: "bg-slate-100 text-slate-700" },
  extracting:           { label: "Extraindo texto", cls: "bg-sky-100 text-sky-800" },
  running_ocr:          { label: "OCR em execução", cls: "bg-indigo-100 text-indigo-800" },
  chunking:             { label: "Quebrando em trechos", cls: "bg-violet-100 text-violet-800" },
  generating_embeddings:{ label: "Gerando embeddings", cls: "bg-fuchsia-100 text-fuchsia-800" },
  indexing:             { label: "Indexando",     cls: "bg-cyan-100 text-cyan-800" },
  ready:                { label: "Pronto",        cls: "bg-emerald-100 text-emerald-800" },
  partially_processed:  { label: "Parcial",       cls: "bg-amber-100 text-amber-800" },
  failed:               { label: "Falhou",        cls: "bg-rose-100 text-rose-800" },
};

function _kbStateBadge(doc) {
  const st = String(doc.state || "").toLowerCase();
  const meta = _KB_STATE_META[st] || { label: st || "?", cls: "bg-slate-100 text-slate-700" };
  return (
    <span
      className={`text-[10.5px] px-1.5 py-0.5 rounded ${meta.cls}`}
      data-testid={`gov-kb-state-${doc.id}`}
    >
      {meta.label}
    </span>
  );
}

export default function GovKnowledgeBase() {
  const [secretarias, setSecretarias] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedSec, setSelectedSec] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [textOpen, setTextOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchHits, setSearchHits] = useState(null);
  const [searching, setSearching] = useState(false);
  const searchDebounce = useRef(null);

  const loadSecs = useCallback(async () => {
    try {
      const { data } = await api.get("/gov/secretarias");
      setSecretarias(data?.secretarias || []);
    } catch (err) {
      /* silencioso */
    }
  }, []);

  const loadDocs = useCallback(async () => {
    setLoading(true);
    try {
      const qs = selectedSec ? `?secretaria=${encodeURIComponent(selectedSec)}` : "";
      const { data } = await api.get(`/gov/knowledge/documents${qs}`);
      setDocuments(data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar documentos");
    } finally {
      setLoading(false);
    }
  }, [selectedSec]);

  useEffect(() => { loadSecs(); }, [loadSecs]);
  useEffect(() => { loadDocs(); }, [loadDocs]);

  // v0.99.17 · Fev/2026 — Auto-refresh quando há docs v2 em processamento.
  // Poll leve a cada 4s enquanto existir doc não-terminal, pra mostrar
  // o progresso real (páginas processadas) sem o operador precisar clicar.
  useEffect(() => {
    const nonTerminal = documents.some((d) => {
      const s = String(d.state || "").toLowerCase();
      return d.kb_version === "v2" && s && s !== "ready" && s !== "failed";
    });
    if (!nonTerminal) return;
    const t = setInterval(() => { loadDocs(); }, 4000);
    return () => clearInterval(t);
  }, [documents, loadDocs]);

  // Busca com debounce
  useEffect(() => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    if (!searchQuery || searchQuery.trim().length < 3) {
      setSearchHits(null);
      return;
    }
    searchDebounce.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.post("/gov/knowledge/search", {
          query: searchQuery.trim(),
          top_k: 8,
          secretaria: selectedSec || undefined,
        });
        setSearchHits(data?.hits || []);
      } catch (err) {
        setSearchHits([]);
      } finally {
        setSearching(false);
      }
    }, 350);
  }, [searchQuery, selectedSec]);

  const docsCount = documents.length;

  return (
    <div className="space-y-6" data-testid="gov-kb-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] text-emerald-700 font-semibold">
            Conhecimento Oficial do Órgão
          </div>
          <h1 className="font-heading text-[26px] font-semibold text-slate-900 mt-1">
            Base de Conhecimento Institucional
          </h1>
          <p className="text-[13px] text-slate-500 mt-1">
            Todos os documentos oficiais alimentam a IA institucional. Nenhuma
            resposta é gerada fora deste acervo.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setTextOpen(true)}
            data-testid="gov-kb-new-text-btn"
            className="px-3 py-2 rounded-md border border-slate-300 text-[12.5px] text-slate-700 hover:bg-slate-50"
          >
            + Novo texto/FAQ
          </button>
          <button
            type="button"
            onClick={() => setUploadOpen(true)}
            data-testid="gov-kb-upload-btn"
            className="px-3 py-2 rounded-md bg-emerald-700 text-white text-[12.5px] hover:bg-emerald-800"
          >
            + Enviar documento
          </button>
        </div>
      </header>

      {/* Filtro por secretaria */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="gov-kb-sec-filter">
        <button
          type="button"
          onClick={() => setSelectedSec("")}
          className={`px-3 py-1.5 rounded-full text-[12px] font-medium ${
            !selectedSec ? "bg-emerald-700 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
          }`}
          data-testid="gov-kb-sec-all"
        >
          Todas
        </button>
        {secretarias.map((s) => (
          <button
            key={s.code}
            type="button"
            onClick={() => setSelectedSec(s.code)}
            data-testid={`gov-kb-sec-${s.code}`}
            className={`px-3 py-1.5 rounded-full text-[12px] font-medium ${
              selectedSec === s.code
                ? "bg-emerald-700 text-white"
                : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Busca */}
      <div className="relative">
        <input
          type="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Buscar na Base Institucional (ex.: 'como emitir IPTU?')"
          data-testid="gov-kb-search-input"
          className="w-full px-4 py-2.5 pr-32 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-emerald-700"
        />
        {searching ? (
          <span className="absolute right-3 top-2.5 text-[11px] text-slate-400">Buscando…</span>
        ) : null}
      </div>

      {/* Resultados da busca (aparecem quando há query >=3 chars) */}
      {searchHits !== null ? (
        <section className="bg-white border border-emerald-200 rounded-xl p-4" data-testid="gov-kb-search-results">
          <div className="text-[11.5px] uppercase tracking-[0.14em] text-emerald-700 font-semibold mb-3">
            {searchHits.length} resultado(s) para &quot;{searchQuery}&quot;
          </div>
          {searchHits.length === 0 ? (
            <div className="text-[12px] text-slate-500">
              Nenhum trecho encontrado. Verifique se há documentos indexados sobre esse tema.
            </div>
          ) : (
            <ul className="space-y-3">
              {searchHits.map((h) => (
                <li key={h.chunk_id} className="border-l-2 border-emerald-400 pl-3">
                  <div className="text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{h.origin?.filename || "Documento"}</span>
                    {h.origin?.secretaria ? (
                      <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-800 rounded text-[10px]">
                        {secretarias.find((s) => s.code === h.origin.secretaria)?.label || h.origin.secretaria}
                      </span>
                    ) : null}
                    <span className="text-slate-400">score {h.score.toFixed(2)}</span>
                  </div>
                  <div className="text-[13px] text-slate-700 mt-1">{h.text}</div>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}

      {/* Lista */}
      <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3 flex items-center justify-between border-b border-slate-100">
          <div className="text-[12.5px] text-slate-500">
            {selectedSec
              ? `${docsCount} documento(s) em ${secretarias.find((s) => s.code === selectedSec)?.label}`
              : `${docsCount} documento(s) na Base`}
          </div>
        </div>
        {loading ? (
          <div className="p-6 text-[12.5px] text-slate-500">Carregando…</div>
        ) : documents.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-[13.5px] text-slate-700 font-medium">
              Ainda não há documentos {selectedSec ? "nesta secretaria" : "na Base"}.
            </div>
            <div className="text-[12px] text-slate-500 mt-1">
              Envie um documento oficial ou importe modelos prontos.
            </div>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="gov-kb-doc-list">
            {documents.map((d) => (
              <DocumentRow
                key={d.id}
                doc={d}
                secretarias={secretarias}
                onChanged={loadDocs}
              />
            ))}
          </ul>
        )}
      </section>

      {uploadOpen ? (
        <UploadModal
          secretarias={secretarias}
          onClose={() => setUploadOpen(false)}
          onSaved={() => { setUploadOpen(false); loadDocs(); }}
        />
      ) : null}
      {textOpen ? (
        <TextModal
          secretarias={secretarias}
          onClose={() => setTextOpen(false)}
          onSaved={() => { setTextOpen(false); loadDocs(); }}
        />
      ) : null}
    </div>
  );
}

function DocumentRow({ doc, secretarias, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [secretaria, setSecretaria] = useState(doc.secretaria || "");
  const [notes, setNotes] = useState(doc.notes || "");
  const [saving, setSaving] = useState(false);

  const label = useMemo(() => {
    return secretarias.find((s) => s.code === doc.secretaria)?.label
      || doc.secretaria_label
      || "Sem secretaria";
  }, [doc, secretarias]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/gov/knowledge/documents/${doc.id}`, {
        secretaria: secretaria || "",
        notes,
      });
      toast.success("Documento atualizado");
      setEditing(false);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!confirm(`Excluir "${doc.original_filename}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/gov/knowledge/documents/${doc.id}`);
      toast.success("Documento excluído");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao excluir");
    }
  };

  const retryProcessing = async () => {
    try {
      await api.post(`/gov/knowledge/documents/${doc.id}/retry`);
      toast.success("Reprocessamento enfileirado");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao reprocessar");
    }
  };

  const isV2 = doc.kb_version === "v2";
  const stateBadge = isV2 ? _kbStateBadge(doc) : null;
  const progressPct = isV2 && doc.total_pages > 0
    ? Math.round((doc.processed_pages / doc.total_pages) * 100)
    : null;

  return (
    <li className="px-5 py-3" data-testid={`gov-kb-doc-${doc.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[13.5px] text-slate-900 font-medium truncate">
            {doc.original_filename}
          </div>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            <span className="text-[10.5px] px-1.5 py-0.5 bg-emerald-100 text-emerald-800 rounded">
              {label}
            </span>
            <span className="text-[10.5px] text-slate-400">
              {(doc.size_bytes / 1024).toFixed(0)} KB · {doc.file_kind}
              {isV2 ? " · v2" : ""}
            </span>
            {stateBadge}
            {isV2 && doc.total_pages > 0 ? (
              <span className="text-[10.5px] text-slate-500">
                {doc.processed_pages}/{doc.total_pages} páginas
                {doc.ocr_used ? " · OCR" : ""}
              </span>
            ) : null}
            {!isV2 && doc.extracted_text_chars ? (
              <span className="text-[10.5px] text-slate-500">
                {doc.extracted_text_chars.toLocaleString("pt-BR")} caracteres indexados
              </span>
            ) : null}
            {!isV2 && !doc.extracted_text_chars ? (
              <span className="text-[10.5px] text-amber-600">Indexação pendente</span>
            ) : null}
          </div>
          {isV2 && progressPct !== null && doc.state !== "ready" && doc.state !== "failed" ? (
            <div className="mt-2 w-full bg-slate-200 rounded-full h-1 overflow-hidden" data-testid={`gov-kb-progress-${doc.id}`}>
              <div
                className="bg-emerald-500 h-1 transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          ) : null}
          {isV2 && doc.error ? (
            <div className="mt-1 text-[11px] text-rose-600 italic truncate" title={doc.error}>
              Falha: {doc.error}
            </div>
          ) : null}
          {doc.notes ? (
            <div className="text-[11.5px] text-slate-500 mt-1 italic">{doc.notes}</div>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          {isV2 && (doc.state === "failed" || doc.state === "partially_processed") ? (
            <button
              type="button"
              onClick={retryProcessing}
              data-testid={`gov-kb-retry-${doc.id}`}
              className="text-[11.5px] text-amber-700 hover:underline"
            >
              Reprocessar
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => setEditing((v) => !v)}
            data-testid={`gov-kb-edit-${doc.id}`}
            className="text-[11.5px] text-emerald-700 hover:underline"
          >
            {editing ? "Cancelar" : "Editar"}
          </button>
          <button
            type="button"
            onClick={remove}
            data-testid={`gov-kb-delete-${doc.id}`}
            className="text-[11.5px] text-rose-600 hover:underline"
          >
            Excluir
          </button>
        </div>
      </div>
      {editing ? (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-[10.5px] uppercase tracking-[0.14em] text-slate-500 font-medium">Secretaria</label>
            <select
              value={secretaria}
              onChange={(e) => setSecretaria(e.target.value)}
              data-testid={`gov-kb-edit-sec-${doc.id}`}
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
            >
              <option value="">Sem secretaria</option>
              {secretarias.map((s) => (
                <option key={s.code} value={s.code}>{s.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10.5px] uppercase tracking-[0.14em] text-slate-500 font-medium">Notas internas</label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid={`gov-kb-edit-notes-${doc.id}`}
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
            />
          </div>
          <div className="md:col-span-2 flex justify-end">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid={`gov-kb-save-${doc.id}`}
              className="px-3 py-2 rounded-md bg-emerald-700 text-white text-[12.5px] hover:bg-emerald-800 disabled:opacity-50"
            >
              {saving ? "Salvando…" : "Salvar"}
            </button>
          </div>
        </div>
      ) : null}
    </li>
  );
}

function UploadModal({ secretarias, onClose, onSaved }) {
  const [file, setFile] = useState(null);
  const [secretaria, setSecretaria] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Selecione um arquivo."); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (secretaria) fd.append("secretaria", secretaria);
      if (notes) fd.append("notes", notes);
      await api.post("/gov/knowledge/documents", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Documento enviado e indexado.");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro no upload");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4" data-testid="gov-kb-upload-modal">
      <form
        onSubmit={submit}
        className="bg-white rounded-xl w-full max-w-md p-6 space-y-4"
      >
        <div>
          <h3 className="font-heading text-lg font-semibold text-slate-900">Enviar documento oficial</h3>
          <p className="text-[12px] text-slate-500 mt-1">
            PDF até 200 MB / 2000 páginas (com OCR automático). Outros formatos até 20 MB.
            Indexação híbrida em background — os textos ficam disponíveis pra busca conforme cada página é processada.
          </p>
        </div>
        <input
          type="file" required
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          data-testid="gov-kb-upload-file"
          className="w-full text-sm"
        />
        <div>
          <label className="text-[10.5px] uppercase tracking-[0.14em] text-slate-500 font-medium">Secretaria</label>
          <select
            value={secretaria}
            onChange={(e) => setSecretaria(e.target.value)}
            data-testid="gov-kb-upload-sec"
            className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
          >
            <option value="">Nenhuma (classificar depois)</option>
            {secretarias.map((s) => (
              <option key={s.code} value={s.code}>{s.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[10.5px] uppercase tracking-[0.14em] text-slate-500 font-medium">Notas internas (opcional)</label>
          <input
            type="text" value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="gov-kb-upload-notes"
            className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-2 rounded-md border border-slate-300 text-sm text-slate-700">
            Cancelar
          </button>
          <button
            type="submit" disabled={saving}
            data-testid="gov-kb-upload-submit"
            className="px-4 py-2 rounded-md bg-emerald-700 text-white text-sm hover:bg-emerald-800 disabled:opacity-50"
          >
            {saving ? "Enviando…" : "Enviar"}
          </button>
        </div>
      </form>
    </div>
  );
}

function TextModal({ secretarias, onClose, onSaved }) {
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [secretaria, setSecretaria] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!title || text.trim().length < 20) {
      toast.error("Título e texto (mín. 20 caracteres) são obrigatórios.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/gov/knowledge/documents/text", {
        title, text, secretaria: secretaria || undefined,
      });
      toast.success("Conteúdo institucional criado e indexado.");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao criar");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4" data-testid="gov-kb-text-modal">
      <form onSubmit={submit} className="bg-white rounded-xl w-full max-w-2xl p-6 space-y-4">
        <div>
          <h3 className="font-heading text-lg font-semibold text-slate-900">Novo conteúdo institucional</h3>
          <p className="text-[12px] text-slate-500 mt-1">
            Ideal para FAQ, comunicados e procedimentos internos curtos.
          </p>
        </div>
        <div>
          <label className="text-[10.5px] uppercase tracking-[0.14em] text-slate-500 font-medium">Título</label>
          <input
            required minLength={3} maxLength={200}
            value={title} onChange={(e) => setTitle(e.target.value)}
            data-testid="gov-kb-text-title"
            className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
            placeholder="Ex.: FAQ — Coleta de Lixo Domiciliar"
          />
        </div>
        <div>
          <label className="text-[10.5px] uppercase tracking-[0.14em] text-slate-500 font-medium">Secretaria</label>
          <select
            value={secretaria} onChange={(e) => setSecretaria(e.target.value)}
            data-testid="gov-kb-text-sec"
            className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
          >
            <option value="">Nenhuma</option>
            {secretarias.map((s) => (
              <option key={s.code} value={s.code}>{s.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[10.5px] uppercase tracking-[0.14em] text-slate-500 font-medium">Conteúdo</label>
          <textarea
            required minLength={20} rows={10}
            value={text} onChange={(e) => setText(e.target.value)}
            data-testid="gov-kb-text-content"
            className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono"
            placeholder="Escreva aqui o texto oficial…"
          />
          <div className="text-[10.5px] text-slate-400 text-right mt-1">
            {text.length.toLocaleString("pt-BR")} caracteres
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-2 rounded-md border border-slate-300 text-sm text-slate-700">
            Cancelar
          </button>
          <button
            type="submit" disabled={saving}
            data-testid="gov-kb-text-submit"
            className="px-4 py-2 rounded-md bg-emerald-700 text-white text-sm hover:bg-emerald-800 disabled:opacity-50"
          >
            {saving ? "Salvando…" : "Salvar"}
          </button>
        </div>
      </form>
    </div>
  );
}
